import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import prestashopLogo from '../../logos/prestashop.png';
import config from '../../config';

const API_URL = config.API_URL;

/**
 * Le formulaire de connexion à PrestaShop : une adresse, une clé, et les boutiques à choisir.
 *
 * Calqué sur `FormulaireAwin` — même signature, même protocole en deux temps (`test` valide et
 * énumère, `connect` enregistre), même portail. PrestaShop se range du même côté : une clé peut
 * ouvrir **plusieurs boutiques**, et l'utilisateur choisit celles qu'il rattache.
 *
 * ## Les trois choses que ce formulaire dit et que les autres ne disent pas
 *
 * 1. **Les ressources que la clé ouvre.** C'est le diagnostic le plus utile de tout l'écran :
 *    une ressource non cochée dans le back-office est la première cause d'un connecteur qui se
 *    connecte et ne rend rien, et l'utilisateur n'a aucun moyen de le deviner — la clé est
 *    valide, la boutique répond, et les chiffres restent vides.
 * 2. **Les états de commande retenus comme « payés ».** Le périmètre n'est pas devinable : les
 *    états sont configurables par boutique. Le montrer avant de connecter évite qu'on découvre
 *    en réunion que le chiffre d'affaires ne compte pas ce qu'on croyait.
 * 3. ⚠️ **La devise de référence, quand elle n'est pas l'euro.** Le bloc Back-office du dashboard
 *    consolidé exprime ses montants en euros, sans porter de devise : une boutique en livres y
 *    verserait des livres dans une colonne libellée €. Le dire ici vaut mieux que de le
 *    découvrir après.
 *
 * @param {boolean} ouvert
 * @param {() => void} onFermer
 * @param {string} token - JWT de l'utilisateur
 * @param {(info: {comptes: object[]}) => void} onConnecte - après enregistrement réussi
 */
function FormulairePrestashop({ ouvert, onFermer, token, onConnecte }) {
  const [form, setForm] = useState({ baseUrl: '', cle: '' });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [boutiques, setBoutiques] = useState([]);
  const [choisies, setChoisies] = useState([]);
  const [connecting, setConnecting] = useState(false);

  const fermer = () => {
    setTestResult(null);
    onFermer?.();
  };

  /** Toute saisie invalide le test précédent : un bouton armé sur une ancienne réponse mentirait. */
  const majForm = (champ) => (e) => {
    setForm(actuel => ({ ...actuel, [champ]: e.target.value }));
    setTestResult(null);
    setBoutiques([]);
    setChoisies([]);
  };

  /**
   * Valide l'adresse et la clé, et propose les boutiques qu'elles ouvrent.
   *
   * Les boutiques déjà rattachées arrivent cochées et désactivées, pour qu'on ne les coche pas
   * en vain.
   */
  const handleTest = async () => {
    if (!form.baseUrl.trim() || !form.cle.trim()) {
      setTestResult({ success: false, error: "Saisissez l'adresse de la boutique et la clé" });
      return;
    }

    setTesting(true);
    setTestResult(null);
    setBoutiques([]);
    setChoisies([]);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/prestashop/test`,
        { baseUrl: form.baseUrl.trim(), cle: form.cle.trim() },
        { headers: { Authorization: `Bearer ${token}` } });

      setTestResult(data);
      setBoutiques(data.boutiques || []);
      // Pré-cocher ce qui n'est pas déjà connecté : c'est presque toujours ce qu'on veut.
      setChoisies((data.boutiques || []).filter(b => !b.alreadyConnected).map(b => b.id));
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || 'Impossible de joindre le serveur',
        // Rendues même sur refus : c'est précisément ce qui manque pour corriger la clé.
        ressources: error.response?.data?.ressources || null,
        // L'adresse de la boutique, quand le serveur a reconnu une URL de back-office.
        adresseProposee: error.response?.data?.adresseProposee || null
      });
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/prestashop/connect`,
        {
          baseUrl: form.baseUrl.trim(),
          cle: form.cle.trim(),
          boutiqueIds: choisies,
          // Le domaine comme nom de connexion. ⚠️ Sans lui, deux boutiques du même client
          // partageraient le nom par défaut, et la seconde **reprendrait** la connexion de la
          // première — la reprise d'une connexion homonyme étant par ailleurs voulue pour une
          // reconnexion.
          connectionName: testResult?.nomPropose
        },
        { headers: { Authorization: `Bearer ${token}` } });

      // Le quota a pu écarter une partie des boutiques : le dire plutôt que de laisser
      // l'utilisateur constater qu'il en manque.
      if (data.nonRetenues > 0) {
        alert(`${data.accounts.length} boutique(s) connectée(s). ${data.nonRetenues} n'ont pas pu l'être : quota de comptes atteint.`);
      }

      setForm({ baseUrl: '', cle: '' });
      setTestResult(null);
      setBoutiques([]);
      setChoisies([]);
      onFermer?.();
      onConnecte?.({ comptes: data.accounts || [] });
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la connexion'
      });
    } finally {
      setConnecting(false);
    }
  };

  if (!ouvert) return null;

  const champ = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px'
  };
  const aide = { fontSize: '11px', color: '#9ca3af', margin: '6px 0 0 0' };
  const etiquette = { display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' };
  const encart = (fond, bord, encre) => ({
    marginTop: '14px', padding: '10px 12px', borderRadius: '8px',
    background: fond, border: `1px solid ${bord}`, color: encre, fontSize: '13px', lineHeight: 1.5
  });

  return ReactDOM.createPortal(
    <div
      onClick={fermer}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '16px', width: '100%', maxWidth: '540px',
          maxHeight: '90vh', overflowY: 'auto', padding: '24px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <img src={prestashopLogo} alt="" style={{ width: '32px', height: '32px' }} />
          <h2 style={{ margin: 0, fontSize: '18px' }}>Connecter PrestaShop</h2>
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 18px 0', lineHeight: 1.5 }}>
          Kokpit lira le <strong>chiffre d'affaires réel</strong> de la boutique — commandes
          encaissées, panier moyen — et le portera dans le bloc Back-office du dashboard
          consolidé, en lecture seule.
        </p>

        <label style={etiquette}>Adresse de la boutique</label>
        <input
          type="text"
          value={form.baseUrl}
          onChange={majForm('baseUrl')}
          placeholder="https://boutique.fr"
          autoComplete="off"
          style={champ}
        />
        <p style={aide}>
          L'adresse publique de la boutique, sans <code>/api</code> — Kokpit l'ajoute.
        </p>

        <label style={{ ...etiquette, marginTop: '14px' }}>Clé du webservice</label>
        <input
          type="password"
          value={form.cle}
          onChange={majForm('cle')}
          placeholder="Clé générée dans le back-office"
          autoComplete="off"
          style={{ ...champ, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        />
        <p style={aide}>
          Back-office &rsaquo; Paramètres avancés &rsaquo; Webservice &rsaquo; Ajouter une clé.
          Cochez en <strong>lecture</strong> au minimum : <code>orders</code>,{' '}
          <code>order_states</code>, <code>currencies</code> — et <code>shops</code> si la
          boutique est multiboutique.
        </p>

        {testResult?.success === false && (
          <div style={encart('#fef2f2', '#fecaca', '#991b1b')}>
            {testResult.error}
            {/* Les ressources ouvertes, même sur refus : c'est ce qui dit quelle case cocher. */}
            {testResult.ressources && testResult.ressources.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.85 }}>
                Ressources actuellement ouvertes par cette clé :{' '}
                {testResult.ressources.join(', ')}
              </div>
            )}

            {/*
              L'adresse de la boutique, quand le serveur a reconnu une URL de back-office. Un
              bouton plutôt qu'une phrase : l'utilisateur vient de coller une adresse, lui
              demander d'en retaper une autre à la main est le moment où il abandonne.
            */}
            {testResult.adresseProposee && (
              <button
                onClick={() => {
                  setForm(actuel => ({ ...actuel, baseUrl: testResult.adresseProposee }));
                  setTestResult(null);
                }}
                style={{
                  marginTop: '10px', padding: '7px 12px', borderRadius: '6px',
                  border: '1px solid #991b1b', background: 'white', color: '#991b1b',
                  fontSize: '12px', cursor: 'pointer'
                }}
              >
                Employer « {testResult.adresseProposee} »
              </button>
            )}
          </div>
        )}

        {/*
          ⚠️ Un avertissement, pas une panne : le connecteur lit la boutique correctement, c'est
          le bloc Back-office qui ne saurait pas quoi faire d'un montant dans une autre devise.
        */}
        {testResult?.deviseIncompatible && (
          <div style={encart('#fffbeb', '#fde68a', '#92400e')}>
            {testResult.deviseIncompatible}
          </div>
        )}

        {/*
          La liste des ressources n'a pas pu être lue, mais les trois indispensables répondent.
          Le dire : promettre une énumération qu'on n'a pas faite serait faux, et l'utilisateur
          doit savoir que la configuration du serveur mérite un coup d'œil.
        */}
        {testResult?.success && testResult.ressourcesEnumerees === false && (
          <div style={encart('#fffbeb', '#fde68a', '#92400e')}>
            La boutique émet des avertissements PHP sur la liste de ses ressources : Kokpit n'a pas
            pu vérifier tout ce que la clé ouvre, mais les commandes, les états et les devises
            répondent. Faites couper l'affichage des erreurs PHP sur le serveur — « Paramètres
            avancés › Performance › Mode debug », ou <code>display_errors</code> côté PHP.
          </div>
        )}

        {testResult?.success && testResult.boutiquesDeduites && (
          <div style={encart('#f0f9ff', '#bae6fd', '#075985')}>
            La ressource <code>shops</code> n'est pas ouverte sur cette clé : Kokpit suppose une
            boutique unique. Si l'installation est multiboutique, cochez <code>shops</code> puis
            testez à nouveau.
          </div>
        )}

        {boutiques.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
              Boutiques à rattacher ({choisies.length}/{boutiques.length})
            </div>
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: '8px',
              maxHeight: '220px', overflowY: 'auto'
            }}>
              {boutiques.map(boutique => (
                <label
                  key={boutique.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: boutique.alreadyConnected ? 'default' : 'pointer',
                    opacity: boutique.alreadyConnected ? 0.55 : 1
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={boutique.alreadyConnected}
                    checked={boutique.alreadyConnected || choisies.includes(boutique.id)}
                    onChange={(e) => setChoisies(actuelles => (
                      e.target.checked
                        ? [...actuelles, boutique.id]
                        : actuelles.filter(id => id !== boutique.id)
                    ))}
                  />
                  <span style={{ flex: 1, fontSize: '13px' }}>{boutique.nom}</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {boutique.alreadyConnected ? 'déjà connectée' : `id ${boutique.id}`}
                  </span>
                </label>
              ))}
            </div>

            {/*
              Le périmètre, montré avant de connecter. Les états sont configurables par boutique :
              personne ne peut deviner ce que « payé » veut dire sur celle-ci.
            */}
            {testResult?.etatsPayes && testResult.etatsPayes.length > 0 && (
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '10px 0 0 0', lineHeight: 1.5 }}>
                Compteront comme encaissées les commandes dans ces états :{' '}
                <strong>{testResult.etatsPayes.join(', ')}</strong>.
              </p>
            )}

            {testResult?.devise?.code && (
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '6px 0 0 0' }}>
                Devise de référence de la boutique : <strong>{testResult.devise.code}</strong>.
              </p>
            )}

            {testResult?.encryptionActive === false && (
              <p style={{ fontSize: '11px', color: '#b45309', margin: '8px 0 0 0' }}>
                Le chiffrement des secrets n'est pas configuré sur ce serveur : la clé sera
                stockée en clair.
              </p>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button
            onClick={fermer}
            style={{
              padding: '9px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
              background: 'white', fontSize: '13px', cursor: 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleTest}
            disabled={testing || !form.baseUrl.trim() || !form.cle.trim()}
            style={{
              padding: '9px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
              background: 'white', fontSize: '13px',
              cursor: testing ? 'wait' : 'pointer'
            }}
          >
            {testing ? 'Test…' : 'Tester la boutique'}
          </button>
          {/* Connexion fermée tant que le test n'a rien proposé : même contrat qu'Awin. */}
          <button
            onClick={handleConnect}
            disabled={connecting || choisies.length === 0}
            style={{
              padding: '9px 16px', borderRadius: '8px', border: 'none',
              background: choisies.length === 0 ? '#e5e7eb' : '#111827',
              color: choisies.length === 0 ? '#9ca3af' : 'white',
              fontSize: '13px',
              cursor: choisies.length === 0 ? 'not-allowed' : (connecting ? 'wait' : 'pointer')
            }}
          >
            {connecting ? 'Connexion…' : `Connecter ${choisies.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FormulairePrestashop;
